import { useEffect, useState, useCallback } from 'react';
import { useEasterEggs } from '@/hooks/useEasterEggs';
import { useLanguage } from '@/i18n/LanguageContext';

const EasterEggOverlay = () => {
  const { state } = useEasterEggs();
  const { t } = useLanguage();
  const [showNotification, setShowNotification] = useState(false);
  const [message, setMessage] = useState('');

  const getDiscoveryMessage = useCallback((id: string): string => {
    const messages: Record<string, string> = {
      konami: t('discovery_konami'),
      secret_sequence: t('discovery_secret_sequence'),
      long_press: t('discovery_long_press'),
      triple_click: t('discovery_triple_click'),
      silence: t('discovery_silence'),
    };
    return messages[id] || t('discovery_label');
  }, [t]);

  useEffect(() => {
    if (state.lastDiscovery) {
      setMessage(getDiscoveryMessage(state.lastDiscovery));
      setShowNotification(true);
      
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [state.lastDiscovery, getDiscoveryMessage]);

  // Konami code visual effect
  if (state.konamiActivated) {
    return (
      <>
        {/* Glitch effect for Konami */}
        <div 
          className="fixed inset-0 pointer-events-none z-50 animate-pulse"
          style={{
            background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,0,0.03) 2px, rgba(0,255,0,0.03) 4px)',
          }}
        />
        
        {showNotification && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
            <div className="px-6 py-3 bg-green-950/80 border border-green-500/30 text-green-400 text-sm">
              {message}
            </div>
          </div>
        )}
      </>
    );
  }

  if (!showNotification) return null;

  return (
    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
      <div className="px-6 py-3 bg-background/95 border border-foreground/20 text-sm">
        <span className="text-muted-foreground/60">{t('discovery_label')}: </span>
        <span className="text-foreground/80">{message}</span>
      </div>
    </div>
  );
};

export default EasterEggOverlay;
